import React, { FC, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AreaDesignerManager } from '../../../../widgets/areaDesigner/areaDesignerManager';
import { Model, findModelColorForModelSide } from '../../state';
import { ModelSide } from '../../state/model/modelSide';

const Container = styled.div`
    width: 100%;
    height: 100%;
`;

class Queue {
    private queue: (() => void)[] = [];
    private executing = false;

    public push(item: () => Promise<void>) {
        this.queue.push(item);
        this.execute();
    }

    public async execute() {
        if (this.executing) return;

        if (this.queue.length > 0) {
            this.executing = true;
            const func = this.queue.shift();
            await func?.();
            this.executing = false;

            this.execute();
        }
    }
}

const queue = new Queue();

const PrintAreaPreview: FC<{
    product: Model;
    modelSide: ModelSide;
}> = ({ modelSide, product }) => {
    const [url, setUrl] = useState<string | null>(null);
    const modelColor = findModelColorForModelSide(product, modelSide);
    const modelOptions = useMemo(
        () => product.options.find((x) => x.colorGuid == modelColor?.guid && (x.sideGuid === modelSide?.guid || x.sideGuid === null)) ?? null,
        [product.options, modelColor]
    );

    const hasPreview = !modelOptions || !modelOptions.isResizable || modelSide.areas.length > 0;

    useEffect(() => {
        queue.push(async () => {
            const canvas = document.createElement('canvas');
            canvas.width = 256;
            canvas.height = 256;
            canvas.style.width = '256px';
            canvas.style.height = '256px';

            const manager = AreaDesignerManager.create(canvas, 'VIEWER');
            manager.setAreas(modelSide.areas);
            manager.setPPCM(modelSide.ppcm);

            if (modelOptions?.isResizable) {
                manager.updateResizable(
                    true,
                    modelOptions.startWidth ?? 1,
                    modelOptions.startHeight ?? 1,
                    modelOptions.startSplitColumns ?? 1,
                    modelOptions.startSplitRows ?? 1
                );
            } else await manager.setSideImage(modelSide.imageUrl);

            const url = canvas.toDataURL('image/png');
            setUrl(url);
        });
        // Redraw whenever anything the canvas depends on changes: the areas, the side image (e.g. after
        // uploading/generating a new one), the ppcm, or the resizable options. Depending only on `areas`
        // left the preview stale when the image changed without touching the areas (e.g. AI "Apply").
    }, [modelSide.areas, modelSide.imageUrl, modelSide.ppcm, modelOptions]);

    return (
        <Container>
            {hasPreview && url && <img src={url} alt='preview' />}
            {!hasPreview && <span>No preview available</span>}
        </Container>
    );
};

export default PrintAreaPreview;
